// src/utils/assignedClinicianFilter.js
//
// Helpers for the clinician-dashboard "Assigned to" Search & Filter dropdown.
//
// WHY THIS EXISTS
// ----------------
// Ops and clinicians need to find "patients this person is on" without leaving
// the dashboard. The filter is client-side over the already-loaded patient
// summaries: Org Admin / Support see every patient in the org, so picking a
// clinician shows that person's caseload; a Clinician user only ever receives
// summaries they are already assigned to (patient_summaries query + Firestore
// rules), so picking a colleague shows the intersection — patients assigned
// to both the viewer and the selected person. We do not add a second "must
// also include me" check; that would hide patients from Org Admins.
//
// Eligibility for the dropdown matches the assign-clinician dialog:
// Clinician + clinical Org. Admin accounts that are not disabled. Pathway
// specialisms are ignored here — this is a find-patients filter, not an
// assignment picker, so an ADHD-only clinician should still be findable.

/**
 * True when this provider account can appear in the "Assigned to" dropdown.
 *
 * Missing `isAssignableClinician` is treated as allowed (legacy Clinician
 * docs predate the flag). Support users are never assignable.
 *
 * @param {object|null|undefined} account A clinicians/{uid} document (or the
 *   dashboard's providerAccountsData row).
 * @returns {boolean}
 */
export function isAssignableProviderAccount(account) {
  if (!account || typeof account !== 'object') return false;
  if (account.accountDisabled === true) return false;
  if (account.isAssignableClinician === false) return false;
  return account.userType === 'Clinician' || account.userType === 'Org. Admin';
}

/**
 * Display name used in the dropdown and the active-filter badge.
 *
 * Prefers "Title First Last" when those fields exist (same as the assign
 * dialogs); falls back to the denormalised clinicianFullName.
 *
 * @param {object} account
 * @returns {string}
 */
export function formatAssignableClinicianLabel(account) {
  if (account?.title && account?.firstName && account?.lastName) {
    return `${account.title} ${account.firstName} ${account.lastName}`;
  }
  return account?.clinicianFullName || 'Unnamed Clinician';
}

/**
 * Build alphabetically sorted react-select options from clinician documents.
 *
 * @param {Array<object>} accounts Rows with `id` or `clinicianId` plus profile fields.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildAssignableClinicianSelectOptions(accounts) {
  if (!Array.isArray(accounts)) return [];

  return accounts
    .filter(isAssignableProviderAccount)
    .map((account) => {
      const value = account.id || account.clinicianId || '';
      return { value, label: formatAssignableClinicianLabel(account) };
    })
    .filter((option) => option.value)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Collect every clinician UID currently assigned to a patient summary.
 *
 * Prefers `assignedClinicianIds` (the field Firestore rules and the dashboard
 * query use). When that array is missing — older optimistic stubs, tests —
 * walks the `clinicians` map including DD supporting IDs so a Lead / OBS /
 * MDT supporting clinician still matches.
 *
 * @param {object|null|undefined} patient A patient_summaries row.
 * @returns {string[]}
 */
export function collectAssignedClinicianIds(patient) {
  if (Array.isArray(patient?.assignedClinicianIds)) {
    return patient.assignedClinicianIds.filter(Boolean);
  }

  const ids = new Set();
  const map = patient?.clinicians;
  if (!map || typeof map !== 'object') return [];

  for (const assignment of Object.values(map)) {
    if (!assignment || typeof assignment !== 'object') continue;
    if (assignment.clinicianId) ids.add(assignment.clinicianId);
    if (Array.isArray(assignment.supportingClinicianIds)) {
      for (const supportingId of assignment.supportingClinicianIds) {
        if (supportingId) ids.add(supportingId);
      }
    }
  }
  return [...ids];
}

/**
 * Dashboard filter predicate: keep the patient when no clinician is selected,
 * otherwise keep them only if that UID is among their assigned clinicians.
 *
 * @param {object} patient
 * @param {string|null|undefined} clinicianId Selected dropdown value.
 * @returns {boolean}
 */
export function matchesAssignedClinician(patient, clinicianId) {
  if (!clinicianId) return true;
  return collectAssignedClinicianIds(patient).includes(clinicianId);
}
